'use client';

import { cn } from '@/lib/utils';
import { motion, useTransform, MotionValue } from 'motion/react';

const CARD_HEIGHT = 420;
const STACK_OFFSET = 8;

interface FocusCardMobileProps {
  icon: React.ComponentType<{ width?: number; height?: number }>;
  title: string;
  text: string;
  index: number;
  scrollYProgress: MotionValue<number>;
  cardsCount: number;
}

export function FocusCardMobile({ 
  icon: IconComponent, 
  title, 
  text, 
  index,
  scrollYProgress,
  cardsCount
}: FocusCardMobileProps) {
  const isFirstCard = index === 0;
  
  // Ogni card ha un range di scroll dedicato
  const cardStart = index / cardsCount;
  const cardEnd = (index + 1) / cardsCount;
  
  // Posizione Y: dal basso (fuori schermo) alla posizione impilata
  const y = useTransform(
    scrollYProgress,
    isFirstCard 
      ? [0, 1] 
      : [cardStart, cardEnd],
    isFirstCard 
      ? [index * STACK_OFFSET, index * STACK_OFFSET] 
      : [500, index * STACK_OFFSET]
  );
  
  // Scale: leggermente più piccola quando arriva, poi normale
  const scale = useTransform(
    scrollYProgress,
    isFirstCard 
      ? [0, 1] 
      : [cardStart, cardEnd],
    isFirstCard 
      ? [1, 1] 
      : [0.95, 1]
  );
  
  // Opacità: appare gradualmente
  const opacity = useTransform(
    scrollYProgress,
    isFirstCard 
      ? [0, 1] 
      : [Math.max(0, cardStart - 0.05), cardStart + 0.05],
    isFirstCard 
      ? [1, 1] 
      : [0, 1]
  );

  return (
    <motion.div
      className='absolute top-0 left-0 right-0'
      style={{
        y,
        scale,
        opacity,
        zIndex: index + 1,
        height: CARD_HEIGHT,
      }}
    >
      <div 
        className={cn('card-gradient-border rounded-3xl shadow-2xl overflow-hidden h-full')}
      >
        <div className={cn('h-full rounded-3xl p-6')} style={{ background: '#1a1a2e' }}>
          <div className='flex flex-col gap-4'>
            <div className='shrink-0'>
              <IconComponent width={60} height={70} />
            </div>
            <div className='flex flex-col flex-1'>
              <h3 className='text-white text-xl font-semibold mb-3 leading-tight'>{title}</h3>
              <p className='text-brand-violet-100 text-sm font-normal leading-relaxed whitespace-pre-line'>
                {text}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
